import React, { useEffect, useMemo, useState } from "react";
import {
  Components as VirtuosoComponents,
  GroupedVirtuoso,
} from "react-virtuoso";
import useRefCallback from "use-ref-callback";
import ConversationListItem from "./conversation-list-item";
import styles from "../../../styles/conversations-list.module.css";
import Image from "next/image";
import noTicketsIcon from "../../../public/no-tickets.svg";
import Text from "@atlas/uicorn/lib/Display/Text";
import useCustomerPortalContext, {
  ConversationStatusIndex,
  ConversationTab,
} from "../customer-portal-context";
import ListGroupHeader from "./list-group-header";
import Spinner from "@atlas/uicorn/lib/Display/Spinner";
import t from "../../../texts/t";

function EmptyState({ language }: { language?: string }) {
  return (
    <div className={styles.emptyState}>
      <Image src={noTicketsIcon} alt={t("No tickets illustration", language)} />
      <Text h="3" color="grey8">
        {t("No tickets here", language)}
      </Text>
      <Text color="grey8">
        {t(
          "Once a new ticket is created, its status will be displayed here.",
          language
        )}
      </Text>
    </div>
  );
}

function LoaderRow() {
  return (
    <div className={styles.loader}>
      <Spinner />
    </div>
  );
}

const ConversationsList = function ConversationsList({
  tab,
  baseUrl,
  language,
}: TConversationsListProps) {
  const {
    loading,
    conversations,
    conversationsCountByStatus,
    getConversations,
    totals,
    filters,
    sorts,
  } = useCustomerPortalContext();
  const [resetting, setResetting] = useState(false);

  const isLoading = loading[tab];
  const conversationsList = conversations[tab];
  const total = totals[tab];
  const virtuosoGroupCounts = conversationsCountByStatus[tab];
  const filter = filters[tab];
  const sort = sorts[tab];

  const handleEndReached = useRefCallback(() => {
    if (total === conversationsList.length) return;
    getConversations(tab);
  });

  useEffect(() => {
    const reset = true;
    setResetting(reset);
    getConversations(tab, reset).finally(() => setResetting(false));
  }, [filter, sort, tab, getConversations]);

  const selectedConversations = conversations[tab];

  const maxNumberCharacters = useMemo(
    () =>
      Math.max(0, ...selectedConversations.map((c) => c.number)).toString()
        .length,
    [selectedConversations]
  );

  const maxNameCharacters = useMemo(
    () =>
      Math.max(
        0,
        ...selectedConversations.map((c) => c.customer.fullName.length)
      ),
    [selectedConversations]
  );

  const virtuosoComponents = useMemo<VirtuosoComponents>(
    () => ({
      Footer: () => (isLoading ? <LoaderRow /> : null),
      TopItemList: ({ children }) => (
        <div className={styles.topItemList}>{children}</div>
      ),
      Group: ({ style, ...props }) => (
        <div className={styles.topItemList} {...props} />
      ),
    }),
    [isLoading]
  );

  const virtuosoGroupContent = useRefCallback((index) => {
    if (virtuosoGroupCounts[index] === 0) return null;

    const status = index === ConversationStatusIndex.open ? "OPEN" : "CLOSED";
    const label = t(
      status === "OPEN" ? "Open tickets" : "Closed tickets",
      language
    );

    return <ListGroupHeader label={label} />;
  });

  if (!conversationsList.length && !isLoading) {
    return <EmptyState language={language} />;
  }

  if (resetting) {
    return <LoaderRow />;
  }

  return (
    <GroupedVirtuoso
      useWindowScroll
      key={`virtuoso-list-${tab}--${filter}-${sort}`}
      groupCounts={virtuosoGroupCounts}
      groupContent={virtuosoGroupContent}
      components={virtuosoComponents}
      endReached={handleEndReached}
      itemContent={(index) => {
        const conversation = conversationsList[index];
        if (!conversation) return;

        return (
          <ConversationListItem
            key={`${index}-${conversation.id}`}
            conversation={conversation}
            baseUrl={baseUrl}
            showCustomer={tab === ConversationTab.account}
            numbersLength={maxNumberCharacters}
            namesLength={maxNameCharacters}
          />
        );
      }}
    />
  );
};

export default ConversationsList;

// #region Types

type TConversationsListProps = {
  tab: ConversationTab;
  baseUrl: string;
  language?: string;
};

// #endregion
